// Edit Preferences Review (approved HTML:
// 15-Edit-Preferences-Consistency-Review-v5.html).
//
// Doug's actual preference model: ordered methods + explicit override.
// Permission + suppression gates remain authoritative. No fallback
// channel is silently selected. Writes are blocked at runtime via
// LIVE_WRITE_GATE_PREFERENCE_UPDATE.
//
// W31 (2026-10-20): on a real session the screen maps the REAL body of
// GET /v1/contacts/:id/preferences (`preferences{voice,text,email}`,
// MEASURED in the W30 audit) instead of dereferencing `payload.order`
// (the crash "reading 'join'"). HubSpot holds no ordered preference for
// any contact (preferred_channel is set on 0 contacts, MEASURED), so the
// ranked list starts empty and honest; the permission list is real.
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { WorkspaceChrome } from '../_shared/WorkspaceChrome.js';
import { DataBoundary, type BoundaryState } from '../_shared/DataBoundary.js';
import { IdentityRow } from '../_shared/IdentityRow.js';
import { Icon } from '../_shared/IconSprite.js';
import { ActionFooter } from '../_shared/ActionFooter.js';
import { RepriorityPill } from '../_shared/PriorityFocus.js';
import { MethodList } from './components/MethodList.js';
import {
  ALL_METHODS,
  INITIAL_ORDER,
  CUSTOMER_WORDS,
  SOURCE_OPTIONS,
  RECORDED_AT,
  SCOPE_COPY,
  type ContactMethod,
  type MethodKey,
} from './mockData.js';
import { SARAH } from '../../data/mockData.js';
import { getReviewReturnTarget } from '../_shared/ReviewState.js';
import { useScopedData } from '../_w2a_lower/scopedData.js';
import {
  isRecord,
  readOptionalString,
  useScreenIdentity,
  type ScreenIdentity,
} from '../_w2a_lower/identity.js';
import { WriteButton } from '../_w2a_lower/liveWrite.js';
import './styles.css';

// Preferences payload. The ranking persists; consent itself is still
// authoritative in Klaviyo and never granted by a preference edit.
export interface PreferencesSeed {
  order: MethodKey[];
  words: string;
  source: string;
  recordedAt: string;
}

export interface PreferencesView {
  methods: ContactMethod[];
  seed: PreferencesSeed;
  /** Honest note shown above an empty ranked list on a real session. */
  emptyNote: string | null;
}

const PREFERENCES_FIXTURE: PreferencesView = {
  methods: ALL_METHODS,
  seed: {
    order: INITIAL_ORDER,
    words: CUSTOMER_WORDS,
    source: SOURCE_OPTIONS[0] ?? '',
    recordedAt: RECORDED_AT,
  },
  emptyNote: null,
};

// Real channel keys (api-server) -> design method keys.
const CHANNEL_TO_METHOD: Array<{ channel: string; key: MethodKey; label: string }> = [
  { channel: 'voice', key: 'phone', label: 'Phone call' },
  { channel: 'text', key: 'text', label: 'Text message' },
  { channel: 'email', key: 'email', label: 'Email' },
];

function toDateTimeLocal(iso: string | null): string {
  if (!iso) return '';
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return '';
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Map the REAL api-server body to the screen view.
 *
 * MEASURED 2026-10-20, GET /v1/contacts/573130771181/preferences:
 *   { contactId, preferences: { voice:{pref:'allowed',dnc:false,blockedReason:null},
 *     text:{...}, email:{...} }, lastUpdatedAt:null, lastUpdatedBy:null,
 *     allowedChannelsNow:{...}, source:'hubspot' }
 *
 * `pref` and `dnc` drive the permission pill; the caption is the real
 * phone / email from the profile. There is no ordered preference in the
 * body, so `order` is empty (honest) rather than the fixture ranking.
 */
export function mapRealPreferences(body: unknown, identity: ScreenIdentity): PreferencesView {
  const prefs = isRecord(body) && isRecord(body.preferences) ? body.preferences : {};
  const profile = identity.profile;
  const methods: ContactMethod[] = [];
  for (const entry of CHANNEL_TO_METHOD) {
    const raw = prefs[entry.channel];
    if (!isRecord(raw)) continue;
    const pref = readOptionalString(raw.pref);
    const dnc = raw.dnc === true;
    const blockedReason = readOptionalString(raw.blockedReason);
    const permission: ContactMethod['permission'] =
      dnc || pref === 'blocked' ? 'blocked' : pref === 'allowed' ? 'granted' : 'needed';
    const caption =
      entry.key === 'email'
        ? profile?.email || 'No email on file'
        : profile?.phone || 'No phone on file';
    const detail =
      blockedReason ??
      (dnc
        ? 'Do not contact'
        : pref === 'allowed'
          ? 'Contact allowed'
          : pref
            ? `Permission: ${pref}`
            : 'Permission unknown');
    methods.push({ key: entry.key, label: entry.label, caption, permission, detail });
  }
  const lastUpdatedAt = isRecord(body) ? readOptionalString(body.lastUpdatedAt) : null;
  return {
    methods,
    seed: {
      order: [],
      words: '',
      source: SOURCE_OPTIONS[0] ?? '',
      recordedAt: toDateTimeLocal(lastUpdatedAt),
    },
    emptyNote: 'No contact preference recorded in HubSpot yet.',
  };
}

export interface EditPreferencesScreenProps {
  stateOverride?: BoundaryState;
}

type Scope = 'ongoing' | 'conversation';

function realScopeCopy(possessive: string): typeof SCOPE_COPY {
  return {
    ongoing: {
      heading: `Update ${possessive} ongoing preference.`,
      detail: 'This becomes the preferred order for future contact.',
    },
    conversation: {
      heading: 'Override only this conversation.',
      detail: 'The ongoing preference stays unchanged after this work ends.',
    },
  };
}

export function EditPreferencesScreen({
  stateOverride,
}: EditPreferencesScreenProps = {}): ReactNode {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const customerId = params.customerId ?? SARAH.id;
  const identity = useScreenIdentity(customerId);
  const scoped = useScopedData<unknown>({
    path: `/v1/contacts/${encodeURIComponent(customerId)}/preferences`,
    fixture: PREFERENCES_FIXTURE,
    loadingLabel: 'Loading preferences…',
    errorTitle: 'Preferences did not load',
    errorCode: 'PREFERENCES_UNAVAILABLE',
    isEmpty: () => false,
    ...(stateOverride !== undefined ? { stateOverride } : {}),
  });
  const useFixture = scoped.previewAllowed;
  const view = useMemo<PreferencesView>(
    () => (useFixture ? PREFERENCES_FIXTURE : mapRealPreferences(scoped.data, identity)),
    [useFixture, scoped.data, identity],
  );
  const { methods, seed } = view;
  const [order, setOrder] = useState<MethodKey[]>(seed.order);
  const [scope, setScope] = useState<Scope>('ongoing');
  const [words, setWords] = useState(seed.words);
  const [source, setSource] = useState(seed.source);
  const [recordedAt, setRecordedAt] = useState(seed.recordedAt);
  useEffect(() => {
    setOrder(seed.order);
    setWords(seed.words);
    setSource(seed.source);
    setRecordedAt(seed.recordedAt);
  }, [seed]);
  const [kind, setKind] = useState<BoundaryState['kind']>(
    stateOverride?.kind ?? 'default',
  );
  const dirty = useMemo(() => {
    return (
      order.join(',') !== seed.order.join(',') ||
      words !== seed.words ||
      source !== seed.source ||
      recordedAt !== seed.recordedAt
    );
  }, [order, words, source, recordedAt, seed]);
  const state =
    stateOverride ??
    (scoped.state.kind === 'default' ? resolveState(kind, undefined, identity.firstName) : scoped.state);
  const first = methods.find(m => m.key === order[0]);
  const scopeCopy = (useFixture ? SCOPE_COPY : realScopeCopy(identity.possessive))[scope];
  const returnTarget = getReviewReturnTarget(location.state, customerId);
  const availableToAdd = methods.filter(m => !order.includes(m.key));
  const customer = identity.customer;
  const firstName = identity.firstName;
  const possessive = identity.possessive;
  const paused = identity.liveWritesPaused;

  return (
    <WorkspaceChrome screen="edit-preferences" rail="my-work">
      {/* W40: the approved 15 has no breadcrumb row (MEASURED, 0 entries);
          the row stays only as the carrier of the preview label. */}
      {scoped.previewAllowed ? (
        <div className="breadcrumbs">
          <div>
            My work <span className="slash">/</span> <strong>{customer.fullName}</strong>{' '}
            <span className="slash">/</span> Edit Preferences
          </div>
          <span className="preview">Design preview · Sample customer</span>
        </div>
      ) : null}
      <section className="ep-hero">
        <div>
          <span className="eyebrow">CUSTOMER RECORD</span>
          <h1>Edit Preferences</h1>
          <p>
            Record the ways {firstName} wants to hear from us, in the order they prefer.
          </p>
        </div>
        <div className="ep-hero-cue">
          <Icon id="message" />
          <div>
            <b>First choice. Then the alternatives.</b>
            <span>One, two or three methods · One clear order</span>
          </div>
        </div>
        {/* W40: approved pill, informational, on every session. */}
        <div className="shared-repriority-heading">
          <RepriorityPill className="shared-repriority" />
        </div>
      </section>
      <IdentityRow
        customer={customer}
        badgeLabel="Contact preferences"
        {...(identity.sinceLabel !== undefined ? { sinceLabel: identity.sinceLabel } : {})}
      />
      <DataBoundary state={state} testId="edit-preferences-boundary">
        <div id="cx-original-surface">
          <div className="ep-section-line">
            <h2>Contact preference</h2>
            <span id="ep-save-state">
              <i /> {dirty ? 'Unsaved changes' : 'All changes saved'}
            </span>
            <button className="text-btn" id="ep-history" type="button">
              View preference history <Icon id="arrow" />
            </button>
          </div>
          <form
            id="ep-preference-form"
            onSubmit={event => {
              event.preventDefault();
            }}
          >
            <div className="ep-grid">
              <div className="ep-stack">
                <section className="card ep-card">
                  <div className="ep-card-head">
                    <div>
                      <Icon id="message" />
                      <h2>Contact methods in order</h2>
                    </div>
                    <span id="ep-method-count">{order.length} method{order.length === 1 ? '' : 's'}</span>
                  </div>
                  <div className="ep-body ep-method-body">
                    <p className="ep-intro">
                      Put {possessive} first choice at the top. Add only the other methods they want
                      to use.
                    </p>
                    {view.emptyNote && order.length === 0 ? (
                      <p role="status" data-testid="edit-preferences-empty" className="muted">
                        {view.emptyNote}
                      </p>
                    ) : null}
                    <MethodList
                      methods={methods}
                      order={order}
                      onReorder={setOrder}
                      onRemove={key => setOrder(prev => prev.filter(k => k !== key))}
                    />
                    <div id="ep-add-methods" className="ep-add-methods">
                      {availableToAdd.map(method => (
                        <button
                          key={method.key}
                          type="button"
                          className="ep-add-method"
                          onClick={() => setOrder(prev => [...prev, method.key])}
                        >
                          + Add {method.label}
                        </button>
                      ))}
                    </div>
                    <div className="ep-method-bottom">
                      <button type="button" className="ep-link" id="ep-no-preference" onClick={() => setOrder([])}>
                        No preference stated
                      </button>
                      <span>Removing a method changes the preference only.</span>
                    </div>
                  </div>
                </section>
                <section className="card ep-card">
                  <div className="ep-card-head">
                    <div>
                      <Icon id="work" />
                      <h2>What {firstName} asked for</h2>
                    </div>
                    <span>Keep the context</span>
                  </div>
                  <div className="ep-body">
                    <label className="ep-field">
                      Customer’s exact words
                      <textarea
                        id="ep-words"
                        name="words"
                        rows={2}
                        required
                        value={words}
                        onChange={event => setWords(event.target.value)}
                      />
                    </label>
                    <div className="ep-fields">
                      <label>
                        Source
                        <select
                          id="ep-source"
                          name="source"
                          value={source}
                          onChange={event => setSource(event.target.value)}
                        >
                          {SOURCE_OPTIONS.map(opt => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        Recorded on <span className="ep-timezone">Central time</span>
                        <input
                          id="ep-recorded-at"
                          name="recordedAt"
                          type="datetime-local"
                          value={recordedAt}
                          required
                          onChange={event => setRecordedAt(event.target.value)}
                        />
                      </label>
                    </div>
                    <div className="ep-form-bottom">
                      <p>
                        <Icon id="check" />
                        Follow any conditions in {possessive} own words.
                      </p>
                      <div>
                        <button
                          className="ep-secondary"
                          type="button"
                          id="ep-discard"
                          disabled={!dirty}
                          onClick={() => {
                            setOrder(seed.order);
                            setWords(seed.words);
                            setSource(seed.source);
                            setRecordedAt(seed.recordedAt);
                          }}
                        >
                          Discard changes
                        </button>
                        <WriteButton
                          paused={paused}
                          className="ep-blue"
                          type="submit"
                          id="ep-review"
                          disabled={!dirty}
                          actionLabel="Review preference change"
                        >
                          Review preference change
                          <Icon id="arrow" />
                        </WriteButton>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
              <aside className="ep-stack">
                <section className="card ep-card">
                  <div className="ep-card-head">
                    <div>
                      <Icon id="target" />
                      <h2>Applies to</h2>
                    </div>
                  </div>
                  <div className="ep-body">
                    <fieldset className="ep-scope-options">
                      <legend>Where should this preference apply?</legend>
                      <label>
                        <input
                          type="radio"
                          name="scope"
                          value="ongoing"
                          checked={scope === 'ongoing'}
                          onChange={() => setScope('ongoing')}
                        />
                        <span>
                          <b>Ongoing contact preference</b>
                          <small>For future conversations with {firstName}</small>
                        </span>
                      </label>
                      <label>
                        <input
                          type="radio"
                          name="scope"
                          value="conversation"
                          checked={scope === 'conversation'}
                          onChange={() => setScope('conversation')}
                        />
                        <span>
                          <b>This conversation only</b>
                          <small>
                            {useFixture
                              ? 'Current action · Conversation C-1048'
                              : 'Current action · This conversation'}
                          </small>
                        </span>
                      </label>
                    </fieldset>
                    <div className="ep-scope-description">
                      <Icon id="target" />
                      <div>
                        <strong id="ep-scope-title">{scopeCopy.heading}</strong>
                        <p id="ep-scope-description">{scopeCopy.detail}</p>
                      </div>
                    </div>
                  </div>
                </section>
                <section className="card ep-card">
                  <div className="ep-card-head">
                    <div>
                      <Icon id="check" />
                      <h2>Permission &amp; contact details</h2>
                    </div>
                  </div>
                  <div className="ep-body ep-permission-body">
                    <div id="ep-permission-list" className="ep-permission-list">
                      {methods.length === 0 ? (
                        <p className="muted" role="status">
                          No channel permissions returned for this contact.
                        </p>
                      ) : null}
                      {methods.map(method => (
                        <div key={method.key} className={`ep-permission-row ${method.permission}`}>
                          <strong>{method.label}</strong>
                          <small>{method.detail}</small>
                        </div>
                      ))}
                    </div>
                    <div className="ep-check-next">
                      <span className="eyebrow">FIRST METHOD TO CHECK</span>
                      <strong id="ep-first-check">{first?.label ?? 'No method selected'}</strong>
                      <p id="ep-check-description">
                        Check permission and contact timing before reaching out.
                      </p>
                    </div>
                    <p className="ep-permission-note">
                      A preference cannot override a blocked channel. Marketing preferences stay in
                      Klaviyo.
                    </p>
                  </div>
                </section>
                <section className="ep-callout">
                  <div className="ep-callout-icon">
                    <Icon id="message" />
                  </div>
                  <div>
                    <strong>An order of preference</strong>
                    <p>
                      Start with the first eligible method. Follow the conversation plan before
                      trying another.
                    </p>
                  </div>
                </section>
              </aside>
            </div>
          </form>
          <section className="next ep-next" aria-label="Next action">
            <span className="eyebrow">UP NEXT</span>
            <div className="avatar">{customer.initials}</div>
            <div>
              <strong>Return to {possessive} current action</strong>
              <p>Continue with the updated contact preference.</p>
            </div>
            <div className="reason">
              <span>Same customer</span>
              <p>Keep the work moving</p>
            </div>
            <Icon id="arrow" />
          </section>
        </div>
      </DataBoundary>
      <ActionFooter
        disabled={dirty}
        noteWhenDisabled="Review or discard the preference change first"
        noteWhenEnabled="Preference saved · Ready to return"
        summaryStrong={`${customer.fullName} · Contact preferences`}
        summaryBody="Removing a method changes the preference only."
        onFinish={() => navigate(returnTarget.path, { state: returnTarget.state })}
      />
      {scoped.previewAllowed && !stateOverride ? (
        <StatePreview kind={kind} onChange={setKind} />
      ) : null}
    </WorkspaceChrome>
  );
}

function resolveState(
  kind: BoundaryState['kind'],
  override: BoundaryState | undefined,
  firstName: string,
): BoundaryState {
  if (override) return override;
  switch (kind) {
    case 'default':
      return { kind: 'default' };
    case 'loading':
      return { kind: 'loading', label: 'Loading preferences…' };
    case 'empty':
      return {
        kind: 'empty',
        title: 'No preferences recorded yet',
        body: `Add the first contact method ${firstName} would like us to use.`,
      };
    case 'error':
      return {
        kind: 'error',
        title: 'Preferences did not load',
        detail: 'The preference service did not respond.',
        errorCode: 'PREFERENCES_UNAVAILABLE',
      };
    case 'degraded':
      return {
        kind: 'degraded',
        title: 'Partial data',
        regions: [{ region: 'ep-permission-list', reason: 'klaviyo_lookup_slow' }],
      };
    case 'blocked':
      return {
        kind: 'blocked',
        title: 'Preference writes are paused',
        reason: 'Live-write gate OFF for Preference Update.',
        gateName: 'LIVE_WRITE_GATE_PREFERENCE_UPDATE',
      };
    case 'midcall':
      return {
        kind: 'midcall',
        label: 'Saving preference…',
        actionName: 'preference_update',
      };
    default:
      return { kind: 'default' };
  }
}

const BOUNDARY_KINDS: Array<BoundaryState['kind']> = [
  'default',
  'loading',
  'empty',
  'error',
  'degraded',
  'blocked',
  'midcall',
];

function StatePreview({
  kind,
  onChange,
}: {
  kind: BoundaryState['kind'];
  onChange: (k: BoundaryState['kind']) => void;
}): ReactNode {
  return (
    <div
      aria-label="Companion state preview"
      style={{
        marginTop: 16,
        padding: 10,
        border: '1px dashed #c4d4e5',
        borderRadius: 6,
        display: 'flex',
        gap: 6,
        flexWrap: 'wrap',
        background: '#f7fbff',
      }}
    >
      <span style={{ fontSize: 9, letterSpacing: 1, color: '#557193', fontWeight: 700 }}>
        PREVIEW STATE:
      </span>
      {BOUNDARY_KINDS.map(k => (
        <button
          key={k}
          type="button"
          onClick={() => onChange(k)}
          aria-pressed={kind === k}
          style={{
            fontSize: 10,
            padding: '4px 8px',
            borderRadius: 4,
            border: '1px solid #c4d4e5',
            background: kind === k ? '#175fac' : '#fff',
            color: kind === k ? '#fff' : '#305780',
            cursor: 'pointer',
          }}
        >
          {k}
        </button>
      ))}
    </div>
  );
}

export default EditPreferencesScreen;
